import React, { useState, useEffect, useRef } from 'react';
import { Navigate, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Target, LogOut, BarChart, Globe, Users, UserCog, Sun, Moon, Sparkles, Activity, TrendingUp, Megaphone, Wallet, Settings, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';

const ALL_NAV_ITEMS = [
  { path: '/dashboard', label: 'Panel', icon: LayoutDashboard, roles: ['admin'] },
  { path: '/leads-crm', label: 'CRM', icon: Users, roles: ['admin', 'cajero'] },
  { path: '/broadcasts', label: 'Broadcasts', icon: Megaphone, roles: ['admin', 'cajero'] },
  { path: '/finanzas', label: 'Finanzas', icon: Wallet, roles: ['admin', 'cajero'] },
  { path: '/mi-configuracion', label: 'Mi config', icon: Settings, roles: ['admin', 'cajero'] },
  { path: '/campaigns', label: 'Campañas', icon: Target, roles: ['admin'] },
  { path: '/wa-landings', label: 'Landings', icon: Globe, roles: ['admin'] },
  { path: '/wa-landing-forensics', label: 'WAForensics', icon: Globe, roles: ['admin'] },
  { path: '/analytics', label: 'Analytics', icon: BarChart, roles: ['admin'] },
  { path: '/ai-generator', label: 'AI Tools', icon: Sparkles, roles: ['admin'] },
  { path: '/user-management', label: 'Usuarios', icon: UserCog, roles: ['admin'] },
  { path: '/meta-diagnostics', label: 'Meta CAPI', icon: Activity, roles: ['admin'] },
  { path: '/meta-insights', label: 'Meta Insights', icon: TrendingUp, roles: ['admin'] },
];

// Rutas que el cajero NO puede visitar
const ADMIN_ONLY_PATHS = [
  '/dashboard', '/campaigns', '/custom-filters', '/reports',
  '/analytics', '/ai-intelligence', '/ai-generator',
  '/click-forensics', '/whatsapp-crm', '/wa-landings', '/wa-landing-forensics',
  '/user-management',
  '/meta-diagnostics',
  '/meta-insights',
];

// Rutas explícitamente permitidas para cajero (whitelist gana siempre).
// Esto evita cualquier confusión cuando agregamos features nuevas:
// si está en esta lista, el cajero siempre puede entrar, incluso si por
// error alguien la sumara a ADMIN_ONLY_PATHS más adelante.
const CAJERO_ALLOWED_PATHS = [
  '/leads-crm',
  '/broadcasts',
  '/finanzas',
  '/mi-configuracion',
];

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || 'admin';
  const { darkMode, toggleTheme } = useTheme();

  const [activeDropdown, setActiveDropdown] = useState(null); // 'marketing' | 'metrics' | 'config' | null
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (activeDropdown && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [activeDropdown]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setActiveDropdown(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  if (!token) return <Navigate to="/login" />;

  // Bloquear rutas admin para cajeros — pero la whitelist gana siempre.
  if (
    role === 'cajero'
    && ADMIN_ONLY_PATHS.includes(location.pathname)
    && !CAJERO_ALLOWED_PATHS.includes(location.pathname)
  ) {
    return <Navigate to="/leads-crm" replace />;
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    navigate('/login');
  };

  const handleDropdownToggle = (key) => {
    setActiveDropdown(prev => prev === key ? null : key);
  };

  const navItems = ALL_NAV_ITEMS.filter(item => item.roles.includes(role));

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-slate-950' : 'bg-gray-100'}`}>
      <nav data-testid="main-nav" className={`shrink-0 border-b backdrop-blur-xl sticky top-0 z-50 transition-colors duration-300 ${
        darkMode ? 'bg-slate-900/90 border-slate-800' : 'bg-white/90 border-slate-200'
      }`}>
          <div className={`w-full mx-auto ${role === 'cajero' ? 'px-4 sm:px-5' : 'max-w-[1440px] px-3 sm:px-4 lg:px-5'}`}>
          <div className={role === 'cajero' ? 'flex h-16 items-center justify-between gap-4' : 'grid h-16 grid-cols-[1fr_auto_1fr] items-center gap-4'}>
            <div className="flex min-w-0 items-center gap-6 justify-self-start">
              <div className="flex shrink-0 items-center gap-2.5">
                <img src="/logo.png" alt="Adphantom" className="h-9 w-9 rounded-lg object-contain" />
                <span className={`text-base font-bold tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>Adphantom</span>
                {role === 'cajero' && (
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide ${darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>Cajero</span>
                )}
              </div>
            </div>
              <div className={`${role === 'cajero' ? 'flex' : 'hidden md:flex'} min-w-0 items-center gap-1.5 ${role === 'cajero' ? '' : 'justify-self-center'}`} ref={dropdownRef}>
                {role === 'cajero' ? (
                  navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    const itemClassName = `flex items-center gap-2 rounded-lg px-3.5 py-2 transition-colors ${
                      isActive
                        ? (darkMode ? 'bg-indigo-500/15 text-indigo-200' : 'bg-indigo-50 text-indigo-700')
                        : (darkMode ? 'text-slate-400 hover:bg-slate-800/70 hover:text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900')
                    }`;

                    if (item.path === '/mi-configuracion') {
                      return (
                        <div key={item.path} className="relative">
                          <button
                            type="button"
                            data-testid={`nav-${item.path.slice(1)}`}
                            onClick={() => handleDropdownToggle('config')}
                            className={itemClassName}
                            aria-expanded={activeDropdown === 'config'}
                            aria-haspopup="menu"
                          >
                            <Icon className="h-4 w-4" />
                            <span className="text-[13px] font-medium">{item.label}</span>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${activeDropdown === 'config' ? 'rotate-180' : ''}`} />
                          </button>

                          {activeDropdown === 'config' && (
                            <div
                              role="menu"
                              className={`absolute right-0 z-50 mt-2 w-56 rounded-xl border p-1.5 shadow-xl ${
                                darkMode ? 'border-slate-700 bg-slate-900 text-slate-200' : 'border-slate-200 bg-white text-slate-700'
                              }`}
                            >
                              <Link
                                to={item.path}
                                onClick={() => setActiveDropdown(null)}
                                className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                                  darkMode ? 'hover:bg-slate-800 hover:text-white' : 'hover:bg-slate-50 hover:text-slate-900'
                                }`}
                                role="menuitem"
                              >
                                <Settings className="h-4 w-4" />
                                <span>Configuración</span>
                              </Link>
                              <button
                                type="button"
                                onClick={() => { toggleTheme(); setActiveDropdown(null); }}
                                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                                  darkMode ? 'hover:bg-slate-800 hover:text-white' : 'hover:bg-slate-50 hover:text-slate-900'
                                }`}
                                role="menuitem"
                                data-testid="config-menu-theme"
                              >
                                {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                                <span>{darkMode ? 'Modo claro' : 'Modo oscuro'}</span>
                              </button>
                              <div className={`my-1.5 border-t ${darkMode ? 'border-slate-800' : 'border-slate-100'}`} />
                              <button
                                type="button"
                                onClick={handleLogout}
                                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                                  darkMode ? 'text-slate-300 hover:bg-red-500/10 hover:text-red-300' : 'text-slate-600 hover:bg-red-50 hover:text-red-600'
                                }`}
                                role="menuitem"
                                data-testid="config-menu-logout"
                              >
                                <LogOut className="h-4 w-4" />
                                <span>Cerrar sesión</span>
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={`nav-${item.path.slice(1)}`}
                        className={`${itemClassName} max-md:hidden`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="text-[13px] font-medium">{item.label}</span>
                      </Link>
                    );
                  })
                ) : (
                  <>
                    {[
                      { path: '/dashboard', label: 'Panel', icon: LayoutDashboard },
                      { path: '/leads-crm', label: 'CRM', icon: Users },
                      { path: '/broadcasts', label: 'Broadcasts', icon: Megaphone },
                      { path: '/finanzas', label: 'Finanzas', icon: Wallet },
                    ].map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-${item.path.slice(1)}`}
                          className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                            isActive 
                              ? (darkMode ? 'bg-slate-800 text-white' : 'bg-teal-100 text-teal-800') 
                              : (darkMode ? 'text-slate-400 hover:text-white hover:bg-slate-800/50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{item.label}</span>
                        </Link>
                      );
                    })}

                    {[
                      {
                        label: 'Marketing',
                        key: 'marketing',
                        icon: Target,
                        items: [
                          { path: '/campaigns', label: 'Campañas', icon: Target },
                          { path: '/wa-landings', label: 'Landings', icon: Globe },
                          { path: '/wa-landing-forensics', label: 'WAForensics', icon: Globe },
                        ]
                      },
                      {
                        label: 'Métricas',
                        key: 'metrics',
                        icon: BarChart,
                        items: [
                          { path: '/analytics', label: 'Analytics', icon: BarChart },
                          { path: '/meta-diagnostics', label: 'Meta CAPI', icon: Activity },
                          { path: '/meta-insights', label: 'Meta Insights', icon: TrendingUp },
                        ]
                      },
                      {
                        label: 'Configuración',
                        key: 'config',
                        icon: Settings,
                        items: [
                          { path: '/user-management', label: 'Usuarios', icon: UserCog },
                          { path: '/ai-generator', label: 'AI Tools', icon: Sparkles },
                          { path: '/mi-configuracion', label: 'Mi config', icon: Settings },
                        ]
                      }
                    ].map((group) => {
                      const GroupIcon = group.icon;
                      const isDropdownOpen = activeDropdown === group.key;
                      const isAnyChildActive = group.items.some(item => location.pathname === item.path);
                      
                      return (
                        <div key={group.key} className="relative">
                          <button
                            onClick={() => handleDropdownToggle(group.key)}
                            className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg transition-colors ${
                              isAnyChildActive
                                ? (darkMode ? 'bg-indigo-500/15 text-indigo-200' : 'bg-indigo-50 text-indigo-700')
                                : (darkMode ? 'text-slate-400 hover:bg-slate-800/70 hover:text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900')
                            }`}
                          >
                            <GroupIcon className="w-4 h-4" />
                            <span className="text-[13px] font-medium">{group.label}</span>
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {isDropdownOpen && (
                            <div className={`absolute left-0 mt-1.5 w-48 rounded-lg border shadow-xl z-50 py-1 transition-all duration-200 ${
                              darkMode ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-205 text-gray-800'
                            }`}>
                              {group.items.map((item) => {
                                const ItemIcon = item.icon;
                                const isItemActive = location.pathname === item.path;
                                return (
                                  <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setActiveDropdown(null)}
                                    className={`flex items-center space-x-2 px-3 py-2 text-sm transition-colors ${
                                      isItemActive
                                        ? (darkMode ? 'bg-slate-850 text-white' : 'bg-teal-50 text-teal-900 font-semibold')
                                        : (darkMode ? 'hover:bg-slate-800/50 text-slate-300 hover:text-white' : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900')
                                    }`}
                                  >
                                    <ItemIcon className="w-4 h-4 shrink-0" />
                                    <span>{item.label}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            <div className={role === 'cajero' ? 'hidden' : 'flex items-center gap-2.5 justify-self-end'}>
              {role !== 'cajero' && (
                <>
                  <Button
                    onClick={toggleTheme}
                    data-testid="theme-toggle"
                    variant="ghost"
                    size="icon"
                    className={`h-9 w-9 rounded-lg ${darkMode ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                    {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  </Button>
                  <Button
                    onClick={handleLogout}
                    data-testid="logout-button"
                    variant="ghost"
                    className={`h-9 rounded-lg px-2.5 ${darkMode ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                    <LogOut className="mr-1.5 h-4 w-4" />
                    Salir
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
      <main>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
